import { Injectable } from '@angular/core';
import { BehaviorSubject, ObservableInput, of } from 'rxjs';
import { DeviceDetectorService } from 'ngx-device-detector';
import { environment } from '../environments/environment';
import { HttpClient } from '@angular/common/http';
import { CartComponent } from './cart/cart.component';
import { StepDefinition } from './dynamic/dynamic';
import { ControlDataFinder } from './dynamic/capture-form/control-util';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';
import { Router } from '@angular/router';

/** 
 * This is the inteface describing the
 */
export interface ApplicationConfig {
  serviceUrl: string,
  themeDir: string, // Local asset directory with default 
  fill?: boolean,
  userIcon?: string,
  title: {
    label: string,
    font?: string,
    size?: string,
  },
  body?: {
    font?: string,
  }
  help?: {
    link: string,
    title: string,
    icon?: string,
  },
  info?: any | {
    link: string,
    logo: string,
    institution?: string,
    preamble?: string,
    displaySearch?: boolean,
    policyUrl?: string;
    emailResource?: string;
    phoneResource?: string;
    emailAdmissions?: string;
    emailAdmText?: string;
    helpText?: string[];
  },
  passwordRegEx?: string,
  passwordHint?: string,
  flair?: boolean,
  flairOverHome?: boolean,
  imageSource?: string,
  backgroundImage?: string,
  registrationImage?: string,
  instructionLink?: string,
  navigationSource?: string,
  emailDomain?: string,
  acceptWaiver?: boolean,
  adminUsers?: string[],
  disableSave?: boolean,
};

export interface ServerServiceConfig extends ApplicationConfig {
  angularVersion: string,
  description: string,
  database: string,
  serviceVersion: string,
  notes: string,
  production?: boolean,
  desktop?: boolean,
  indeterminateCheckbox?: boolean,
  theme?: any,
  serviceDescription?: string,
  serviceStarted?: Date,
  integrations?: any[],
  acceptWaiver?: boolean;
  lookups?: OptionSet,
  layoutDefinitions?: LayoutInterface[],
  defaults?: any,
  studentTypeTrigger?: string,
};

export interface OptionSet {
  [key: string]: OptionDetail[]
}

export interface OptionDetail {
  id: number,
  category: string,
  value: string,
  label: string,
  parent?: string
}

export interface LoginData {
  username: string,
  firstname: string,
  lastname: string,
  email: string,
  phone: string,
  nickname: string,
  editor?: boolean,
  lastCluster?: string | null | undefined,
  program?: string | null | undefined,
  defaultLayout?: LayoutInterface | null | undefined,
}

export interface LayoutInterface {
  layoutId: number,
  layoutKey: string,
  submitRoute?: any,
  defaultRole?: number,
  tab?: string | number,
  public?: boolean,
  data?: any,
  field?: FormlyFieldConfig,
  linear?: boolean,
  [extra: string]: any
}

export interface RoleInterface {
  email: string,
  role: number,
  error?: string
}

@Injectable({
  providedIn: 'root'
})
export class RemoteDisplayValue {
  public fieldId: string | undefined = '';
  public currentValue: string | null | undefined;
  public step: string | undefined;
  private _values: BehaviorSubject<any> = new BehaviorSubject<any>('');
  public displayValue = this._values.asObservable();

  constructor() { }

  setDisplayFromLocal(ui: ControlDataFinder | undefined) {
    this._values.next(this.currentValue = (ui?.getValue(this.step, this.fieldId)));
  }
  setDisplayFromSet(dataSet: any) {
    this._values.next(this.currentValue = dataSet[this.fieldId ?? '']);
  }
  setDisplay(displayLabel: string) {
    this._values.next(this.currentValue = displayLabel);
  }
}
export class ObservableMessage {
  subject: BehaviorSubject<any>;
  observed: ObservableInput<any>;
  constructor(public value: any) {
    this.subject = new BehaviorSubject(value);
    this.observed = this.subject.asObservable();
  }
  subscribe(callback: any): Subscription {
    return this.subject.subscribe(callback);
  }
  next(value: any) {
    return this.subject.next(value);
  }
  getValue(): any {
    return this.subject.value;
  }
}

@Injectable({
  providedIn: 'root'
})
export class ContextService {
  public clientIp: string = '0.0.0.0';
  public setClientIp(address: string) {
    this.clientIp = address;
  }
  public config: ServerServiceConfig | undefined;

  private layoutDefinitions: LayoutInterface[] = [];

  public layout: LayoutInterface | undefined | null = null;
  private delegatedLayout: LayoutInterface | undefined | null;
  public delegatedLogin: LoginData | undefined | null;

  private messageSource: any = {
    "adminEmail": new ObservableMessage(null),
    "applicationNumber": new ObservableMessage('NEW'),
    "applicationInfo": new ObservableMessage(null),
    "canProgress": new ObservableMessage(true),
    "checkValidity": new ObservableMessage(false),
    "closeDelegate": new ObservableMessage(false),
    "deviceValue": new ObservableMessage(null),
    "dropListTargets": new ObservableMessage([]),
    "failedLogin": new ObservableMessage(false),
    "fileSaved": new ObservableMessage(null),
    "formComplete": new ObservableMessage({ valid: false, label: null }),
    "formEditor": new ObservableMessage(false),
    "indeterminateProgress": new ObservableMessage(false),
    "initiatePayment": new ObservableMessage(false),
    "instructionsLink": new ObservableMessage(''),
    "layoutIdentity": new ObservableMessage(null),
    "loginData": new ObservableMessage(null),
    "loginRequest": new ObservableMessage(false),
    "messageSource": new ObservableMessage(''),
    "orientation": new ObservableMessage( window.innerWidth < 950 ),
    "pageSubtitle": new ObservableMessage(''),
    "percentComplete": new ObservableMessage(0),
    "processSubmit": new ObservableMessage(null),
    "readyToReceive": new ObservableMessage(false),
    "returnToAdmin": new ObservableMessage(false),
    "showProgressValue": new ObservableMessage(true),
    "selectedData": new ObservableMessage(null),
    "stepIndex": new ObservableMessage(null),
    "waiverResult": new ObservableMessage(false),
    "logout": new ObservableMessage(false),
  };

  private cart = new CartComponent();
  private subscriptions: Subscription[] = [];

  constructor(private deviceService: DeviceDetectorService, private http: HttpClient, private router: Router,
    private motd: MatSnackBar) {
    this.nextMessage("deviceValue", this.deviceService);
    if (!!this.deviceService.isDesktop()) {
      this.unsetLoginData();
    } else {
      this.loadLocalData(true);
    }
  }

  isMobile() : boolean {
    return this.deviceService.isMobile();
  }
  validateWaiver(): void {
    // This sets the user's the waiver validity based on the userId and expiration date
    try {
      this.http.get<{ isValid: boolean, description: string }>('$service/validate-waiver').subscribe({
        error: (err) => {
          console.log('Waiver validation error %O', err);
        },
        next: (result: { isValid: boolean, description: string }) => {
          console.log('Waiver validation result %O', result);
          this.nextMessage("waiverResult", result.isValid);
        }
      });
    } catch (err) {
      console.error('Unable to validate waiver %O', err);
    }
  }

  subscribeTo(messageId: string, callback: any): Subscription {
    if (!this.messageSource[messageId]) {
      throw new Error(`subscribeTo ${messageId} is not a message type`);
    }
    return this.messageSource[messageId].subscribe(callback);
  }

  nextMessage(messageId: string, value: any) {
    if (!this.messageSource[messageId]) {
      throw new Error(`nextMessage ${messageId}`);
    }
    return this.messageSource[messageId].next(value);
  }

  getMessageValue(messageId: string): any {
    if (!this.messageSource[messageId]) {
      throw new Error(`getMessageValue ${messageId}`);
    }
    return this.messageSource[messageId].getValue();
  }

  addNewMessage( messageId: string, defaultValue: unknown) {
    this.messageSource[messageId] = new ObservableMessage(defaultValue);
  }
  expandVariables(sourceString: string, variables: any): string {
    let target = sourceString, matches = sourceString?.match(/\${(.*)}/g);
    if (matches) {
      matches.forEach(match => {
        target = target.replace(match, this.config?.info?.[match?.substring(2, match?.length - 1)] ?? '');
      });
    }
    return target;
  }

  /** 
   * Called by each step when after loading of the form should have made the controls
   * The sent value is whether all steps are in that state
   */
  processedSteps(steps: StepDefinition[], stepCalling: StepDefinition | undefined) {
    if (stepCalling) {
      stepCalling.isLoaded = true;
      if (steps.every(step => step.isLoaded)) {
        setTimeout( () => this.nextMessage("readyToReceive", true), 10);
      }
    }
    console.log('Original Timeout call %O', stepCalling?.step);
  }
  /* On mobile devices and in in certain cases, load the data from the browser */
  loadLocalData(login: boolean = true) {
    try {
      let value = sessionStorage.getItem('config');
      if (!!value) {
        try {
          this.setApplicationInformation(JSON.parse(value));
        } catch (err) {
          console.error('not a valid JSON value %O', value);
        }
      }
      value = sessionStorage.getItem('layout');
      if (!!value) {
        let objValue: LayoutInterface;
        try {
          objValue = JSON.parse(value);
        } catch (err) {
          objValue = { layoutId: parseInt(value), layoutKey: 'UNKNOWN' }
        }
        this.setLayoutId(objValue);
      }
      if (login) {
        value = sessionStorage.getItem('login');
        if (!!value) {
          this.setLoginData(JSON.parse(value));
        } else {
          this.unsetLoginData();
        }
      }
    } catch (err: any) {
      console.error('Unable to load local config data %O', err.message);
      this.unsetLoginData();
    }
  }

  async sleep(ms: number) {
    await new Promise(resolve => setTimeout(resolve, ms));
  }

  async isAdminEmail(email: string) {
    this.http.get<RoleInterface[]>('$service/email-role', { params: { email: email } }).subscribe({
      error: (err) => {
        console.log('User id role check error %O', err);
        this.nextMessage("adminEmail", { email: email, role: 0 });
      },
      next: (result: RoleInterface[]) => {
        this.nextMessage("adminEmail", result[0]);
      }
    });
  }

  /**
   * Convert the array of strings into a camel-case string (used in titling)
   * @param stringArray 
   */
  toCamelCase(stringArray: string[]) {
    let finalString = '';

    stringArray.forEach((word) => {
      finalString += word.substring(0, 1).toUpperCase() + word.substring(1) + ' ';
    })
    return finalString?.trim();
  }
  /**
   * Convert a string with dashes into individual words 
   * @param inputString 
   */
  toWords(inputString: string): string[] | null {
    return inputString.match(/[A-Z\xC0-\xD6\xD8-\xDE]?[a-z\xDF-\xF6\xF8-\xFF]+|[A-Z\xC0-\xD6\xD8-\xDE]+(?![a-z\xDF-\xF6\xF8-\xFF])|\d+/g);
  }

  /**
   * Called after a theme is applied via the config. 
   * This sets the color theme for the underlying angular theme 
   * @param theme css-name/css-value pairs from the version service 
   * @param sytle is the targeted css set to be modified 
   */
  loadPropertyVariables(theme: any, style: CSSStyleDeclaration) {
    if (!!theme && !!style) {
      Object.keys(theme).forEach(cssName => {
        style.setProperty(cssName, theme[cssName]);
      });
    }
  }
  /**
   * Inject a script into the HTML document 
   * @param path 
   */
  loadScript(path: string, cb: any = null) {
    let node: HTMLScriptElement = document.createElement('script'); // creates the script tag
    node.src = path;
    node.type = 'text/javascript'; // set the script type
    node.async = true; // makes script run asynchronously
    node.onload = cb;
    document.getElementsByTagName('head')[0].appendChild(node);
  }
  /**
   * Delegation 
   * @param layout 
   */
  setDelegateLayout(layout: LayoutInterface | null | undefined) {
    if (!!layout) {
      this.delegatedLayout = this.layout;
      this.layout = layout;
    } else {
      this.layout = this.delegatedLayout;
      this.delegatedLayout = null;
    }
  }
  revertDelegate() {
    //  let adminLayout = this.delegatedLayout;
    this.delegatedLayout = null;
    if (!!this.delegatedLogin) this.delegatedLogin.defaultLayout = null;
    this.setLoginData(this.delegatedLogin);
    this.delegatedLogin = null;
    this.nextMessage("returnToAdmin", true);
  }
  prepareToDelegate() {
    this.nextMessage("returnToAdmin", false);
    this.delegatedLogin = this.messageSource["loginData"].getValue();
  }
  /**
   * Return the definition of the layout from the service layer
   * 
   * @param layoutKey string key of the layout
   * @returns LayoutInterface
   */
  findLayout(layoutKey: string): LayoutInterface | undefined | null {
    return this.layoutDefinitions?.find(layout => layout?.layoutKey == layoutKey);
  }

  findLayoutIndex(layoutId: number): LayoutInterface | undefined | null {
    return this.layoutDefinitions?.find(layout => layout?.layoutId == layoutId)
  }

  selectedRow(data: any) { this.nextMessage("selectedData", data); }
  allowNext(value: boolean) { this.nextMessage("canProgress", value); }
  setProgressIndeterminate(isIndeterminate: boolean) { this.nextMessage("indeterminateProgress", isIndeterminate); }
  showProgress(isVisible: boolean) {
    this.nextMessage("showProgressValue", isVisible);
  }
  /** Called by the app loading injector */
  setApplicationInformation(config: ServerServiceConfig) {
    this.config = Object.assign({
      desktop: this.deviceService.isDesktop(),
      production: environment.production
    }, config);

    console.log('Application production %O', this.config.production);

    if (!config.instructionLink) {
      config.instructionLink = '$service/instructions/default-welcome.md';
    }
    this.setInstructions(config.instructionLink);

    if (!config.navigationSource) {
      config.navigationSource = 'Apply';
    }
    // this.setNavigationSource(config.navigationSource);

    if (!!config.layoutDefinitions) {
      this.layoutDefinitions = config.layoutDefinitions;
    }
    if (!this.deviceService.isDesktop()) {
      sessionStorage.setItem('config', JSON.stringify(config));
    }
    this.nextMessage("applicationInfo", this.config);
  }

  /**
   * Send the new identifier of the application to listening sections
   * 
   * @param id string 
   */
  setApplicationNumber(id: string) {
    this.setDefaultValue('appId', id);
    this.nextMessage("applicationNumber", id);
  }

  setDefaultValue(name: string, value: any) {
    if (!!this.config) {
      if (!this.config?.defaults) {
        this.config.defaults = {};
      }
      this.config.defaults[name] = value;
    }
  }
  /**
   * 
   * @param source 
   * @returns 
   */
  substituteVariable(source: string) {
    if (!!source && typeof source === 'string') {
      let matches = source.match(/({{[\w-|]+}})/g);
      if (!!matches) {
        matches.forEach(match => {
          let variable = match.slice(2, -2);
          source = source.replace(match, this.config?.defaults[variable]);
        });
      }
    }
    return source;

  }
  /**
   * This changes which step has focus
   * @param layout 
   * @param index 
   */
  setStepIndex(layout: LayoutInterface | undefined | null, stepName: string | null = null, field: FormlyFieldConfig | null = null) {

    if (!!this.layout && !!field) {
      this.layout.tab = field?.props?.stepName ?? stepName;
    }
    if (!!field) {
      let oldClass = field.className;
      field.className += ' highlight';
      if (!!this.layout) { this.layout.field = field; }
      if (!!field.props) {
        field.props.blur = (fld) => {
          fld.className = oldClass;
          if (!!fld.props) {
            delete fld.props.blur;
          }
        }
      }
    } else if (!!this.layout?.field) {
      this.layout.field.className = this.layout.field.props?._oldClass ?? null;
      delete this.layout.field;
    }
    if (!!layout) {
      this.nextMessage("stepIndex", { layout: layout, step: field?.props?.stepName ?? stepName });
    }
  }


  /**
   * This resets the application to the new layout
   * @param layout 
   */
  setLayoutId(layout: LayoutInterface | null | undefined) {
    if (!this.delegatedLayout) {
      let isSame = (this.layout?.layoutKey === undefined && layout?.layoutKey === undefined) || (this.layout?.layoutKey ?? '').localeCompare(layout?.layoutKey ?? '') == 0;

      this.layout = layout;
      if (!this.deviceService.isDesktop()) {
        sessionStorage.setItem('layout', JSON.stringify(layout));
      }
      if (!isSame) {
        this.nextMessage("layoutIdentity", layout);
      }
    }
  };

  getCartCount() {
    return this.cart.cartCount;
  };

  updateCartCount() {
    this.cart.cartCount++;
  };

  changeMessage(message: string, statusType: string = 'OK') {
    if (message != this.getMessageValue("messageSource")) {
      if (message == '') {
        this.motd.dismiss();
      } else {
        this.motd.open(message, statusType);
      }
      this.nextMessage("messageSource", message);
    }
  }
  setOrientation(orientation: string) { this.nextMessage("orientation", (orientation === 'vertical')); }
  setInstructions(link: string) { this.nextMessage("instructionsLink", link); }
  setSubTitle(subtitle: string) { this.nextMessage("pageSubtitle", subtitle); }
  // setNavigationSource(navigation: string) { this.navigationSource.next(navigation); }
  setIsValid(valid: any) { this.nextMessage("formComplete", valid); }
  setPercentComplete(value: number) {
    if (value < 0) value = 0;
    if (value > 100) value = 100;
    this.nextMessage("percentComplete", value);
  }

  /** Debugging */
  dumpFormData() { console.info('\n  **** Form Data ****\n%O\n  ******* END *******', this.layout?.data ?? 'NO LAYOUT'); }

  /** Login context functions */
  setLoginData(login: LoginData | undefined | null) {
    if (!this.deviceService.isDesktop()) {
      sessionStorage.setItem('login', JSON.stringify(login));
    }
    this.nextMessage("loginData", login);
  }

  pleaseLogin(popup: boolean = true) {
    this.setFailedLogin(false);
    this.nextMessage("loginRequest", popup);
  }
  unsetLoginData() {
    this.setStepIndex(this.layout);
    this.setLoginData({
      username: '',
      firstname: '',
      lastname: '',
      email: '',
      phone: '',
      nickname: '',
      lastCluster: '',
      defaultLayout: { layoutId: 0, layoutKey: 'Register User', submitRoute: [{ service: 'register' }] },
      editor: false,
    });
    this.setApplicationNumber('NEW');
    this.setPercentComplete(0);
    this.setLayoutId(null);
    this.selectedRow(null);
  }
  __clearSession() {
    this.unsetLoginData();
    this.setPercentComplete(0);
    sessionStorage.clear();
    //this.router.navigate(['/apply']);
  }

  setFailedLogin(failedLogin: boolean) {
    this.nextMessage("failedLogin", failedLogin);
  }

  activateEditor(editor: boolean) { this.nextMessage("formEditor", editor); }

  /** WIP - EDITOR */
  setDropListTargets(list: string[]) { this.nextMessage("dropListTargets", list); }

  async reloadServices(localConfig: ApplicationConfig) {
    let reloadComplete = false;
    // Received the response we want, set the data locally for use, ensure chat is set to use the data
    if (!localConfig.serviceUrl) {
      localConfig.serviceUrl = 'http://localhost:8080'; // DEFAULT
    }
    // Now we subscribe to the service version request 
    this.http.get<ServerServiceConfig>(localConfig.serviceUrl + '/version', { params: { theme: localConfig.themeDir } }).subscribe({
      error: (httpError: { status: number; }): ObservableInput<{}> => {
        this.changeMessage('Enable to connect to serivce');
        console.log('Error getting version information %O', httpError);
        return of(false);
      },
      next: (serviceConfig: ServerServiceConfig) => {
        localConfig = Object.assign(localConfig, serviceConfig);
        reloadComplete = !!serviceConfig.database;
        this.setApplicationInformation(<ServerServiceConfig>localConfig);
        return of(reloadComplete);
      }
    });
  }
}